import React, { useState, useEffect, useCallback } from 'react';
import { Package2, AlertTriangle, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { api } from '../../services/api';

const COVERAGE_STYLES = {
  'Coperto': { badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  'Parzialmente coperto': { badge: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle },
  'Non coperto': { badge: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
  'N/A': { badge: 'bg-slate-100 text-slate-700 border-slate-300', icon: Lock },
};

/**
 * Disponibilità grezzo panel usable inside Order/Order Line detail.
 * Props: productId (required), orderLineId (optional), requiredQty (required),
 *        allowReserve (default true), onChange (callback after reservation)
 */
export default function RawAvailabilityPanel({ productId, orderLineId, requiredQty, allowReserve = true, onChange }) {
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [reserveQty, setReserveQty] = useState('');

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await api.get('/raw-materials/availability', {
        params: { product_id: productId, required_qty: Number(requiredQty) || 0 },
      });
      setAvail(res.data);
      if (orderLineId) {
        const rr = await api.get('/raw-materials/reservations', { params: { order_line_id: orderLineId } });
        const active = (rr.data || []).find(x => x.status === 'Attiva' || x.status === 'Parzialmente scaricata');
        setReservation(active || null);
      }
    } catch (err) {
      // silently ignore, panel is optional
    } finally {
      setLoading(false);
    }
  }, [productId, orderLineId, requiredQty]);

  useEffect(() => { load(); }, [load]);

  const reserve = async () => {
    if (!orderLineId) return toast.error('Riga d\'ordine non specificata');
    const q = Number(reserveQty);
    if (!q || q <= 0) return toast.error('Quantità non valida');
    try {
      await api.post('/raw-materials/reservations', { order_line_id: orderLineId, reserved_quantity: q });
      toast.success('Grezzo prenotato');
      setReserveQty('');
      load();
      onChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.availability) {
        toast.error(`${detail.message} — libero: ${detail.availability.libera}`);
      } else {
        toast.error(String(detail || 'Errore prenotazione'));
      }
    }
  };

  const cancelReservation = async () => {
    if (!reservation) return;
    try {
      await api.post(`/raw-materials/reservations/${reservation.id}/cancel`, { reason: 'Annullamento manuale' });
      toast.success('Prenotazione annullata');
      load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  if (!productId) return null;

  if (loading && !avail) {
    return <div className="text-xs text-slate-400">Caricamento disponibilità grezzo…</div>;
  }
  if (!avail) return null;

  const cov = COVERAGE_STYLES[avail.stato_copertura] || COVERAGE_STYLES['N/A'];
  const CovIcon = cov.icon;

  return (
    <Card className="border-slate-200" data-testid="raw-availability-panel">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package2 className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-800">Disponibilità grezzo</span>
            {avail.raw_part_code && <Badge variant="outline" className="font-mono text-xs">{avail.raw_part_code}</Badge>}
          </div>
          <Badge className={`${cov.badge} border text-xs`} data-testid="coverage-badge">
            <CovIcon className="w-3 h-3 mr-1" /> {avail.stato_copertura}
          </Badge>
        </div>

        {!avail.raw_part_code && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Il prodotto non ha un <b>Codice grezzo</b> definito. Impostalo nella scheda prodotto per abilitare il controllo grezzo.
          </div>
        )}

        {avail.raw_part_code && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-center">
            <MiniStat label="Disponibile" value={avail.disponibile} tone="neutral" />
            <MiniStat label="Riservato" value={avail.riservata} tone="info" />
            <MiniStat label="Libero" value={avail.libera} tone="good" highlight />
            <MiniStat label="Richiesto" value={avail.richiesta} tone="neutral" />
            <MiniStat label="Mancante" value={avail.mancante} tone={avail.mancante > 0 ? 'bad' : 'neutral'} />
          </div>
        )}

        {avail.mancante > 0 && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2" data-testid="insufficient-warning">
            <AlertTriangle className="w-4 h-4" />
            Grezzo insufficiente per evadere l&apos;ordine (mancano <b>{avail.mancante}</b>).
          </div>
        )}

        {orderLineId && allowReserve && avail.raw_part_code && (
          <div className="border-t pt-2">
            {reservation ? (
              <div className="flex items-center justify-between text-sm">
                <div>
                  Prenotazione attiva: <b>{reservation.reserved_quantity}</b>
                  {reservation.unloaded_quantity > 0 && <> · Scaricato: <b>{reservation.unloaded_quantity}</b></>}
                  <Badge variant="outline" className="ml-2 text-xs">{reservation.status}</Badge>
                </div>
                {reservation.status !== 'Scaricata' && (
                  <Button size="sm" variant="outline" onClick={cancelReservation} data-testid="cancel-reservation-btn">
                    Annulla prenotazione
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 block">Prenota grezzo per questa riga</label>
                  <Input type="number" value={reserveQty} onChange={e => setReserveQty(e.target.value)}
                    placeholder={`Es: ${avail.richiesta}`} data-testid="reserve-qty-input" />
                </div>
                <Button size="sm" onClick={reserve} data-testid="reserve-btn">Prenota</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, tone, highlight }) {
  const toneClasses = {
    good: 'text-emerald-700', warn: 'text-amber-700', info: 'text-blue-700',
    bad: 'text-red-700', neutral: 'text-slate-800',
  }[tone] || 'text-slate-800';
  return (
    <div className={`rounded border ${highlight ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'} p-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm font-semibold ${toneClasses}`}>{Number(value || 0).toLocaleString('it-IT')}</div>
    </div>
  );
}
